import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ThumbsUp, ThumbsDown, Bookmark } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ApiError } from '@/api/client'
import { useMessageFeedback, useMyFeedback } from '@/hooks/useMessages'
import { useCreateBookmark, useBookmarkedMessages } from '@/hooks/useBookmarks'
import { errorToast } from '@/hooks/errorToast'
import type { MessageResponse } from '@/api/types_session'

interface FeedbackButtonsProps {
  message: MessageResponse
  disabled?: boolean
}

export function FeedbackButtons({ message, disabled = false }: FeedbackButtonsProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const feedbackMutation = useMessageFeedback()
  const createBookmark = useCreateBookmark()
  const { getBookmarkForMessage } = useBookmarkedMessages()

  // The caller's own rating, read from the message's feedback list upstream.
  const myRating = useMyFeedback(message.session, message.id)
  const [feedbackGiven, setFeedbackGiven] = useState<'positive' | 'negative' | null>(null)
  const rating = feedbackGiven ?? myRating
  // Track locally created bookmarks (before query cache updates)
  const [localBookmarkId, setLocalBookmarkId] = useState<string | null>(null)

  // Check if message is already bookmarked (from server or local creation)
  const existingBookmark = getBookmarkForMessage(message.id)
  const bookmarkId = existingBookmark?.id || localBookmarkId
  const isBookmarked = !!bookmarkId

  const handleFeedback = async (type: 'positive' | 'negative') => {
    if (rating || feedbackMutation.isPending || disabled) return
    setFeedbackGiven(type)
    // Retry on 404 — the message may not be readable yet if streaming just finished
    const maxRetries = 3
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        await feedbackMutation.mutateAsync({
          sessionId: message.session,
          messageId: message.id,
          type,
        })
        return
      } catch (err: unknown) {
        const is404 = err instanceof ApiError && err.status === 404
        if (is404 && attempt < maxRetries - 1) {
          await new Promise(r => setTimeout(r, 500 * (attempt + 1)))
          continue
        }
        // All retries exhausted - show error toast
        errorToast({
          context: 'messageFeedback',
          description: t('feedback.failedDescription'),
          onRetry: () => void handleFeedback(type),
        })
        setFeedbackGiven(null)
        return
      }
    }
  }

  const handleBookmark = () => {
    // If already bookmarked, navigate to the bookmark detail page
    if (isBookmarked && bookmarkId) {
      void navigate(`/bookmarks/${bookmarkId}`)
      return
    }
    // Otherwise create a new bookmark
    createBookmark.mutate(
      { message_id: message.id, session_id: message.session },
      {
        onSuccess: (newBookmark) => {
          setLocalBookmarkId(newBookmark.id)
        },
      }
    )
  }

  // Base button styles with 44px minimum tap target (iOS accessibility)
  const buttonBase = cn(
    "min-w-[44px] min-h-[44px] p-2.5",
    "rounded-xl",
    "flex items-center justify-center",
    "transition-colors duration-150",
    "disabled:opacity-40"
  )

  return (
    <div className="mt-3 pt-3 border-t border-neutral-200/40 flex items-center gap-2">
        <button
          type="button"
          onClick={() => handleFeedback('positive')}
          disabled={disabled || feedbackMutation.isPending || rating !== null}
          className={cn(
            buttonBase,
            rating === 'positive'
              ? "bg-primary/10 text-primary"
              : "text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 active:bg-neutral-200"
          )}
          aria-label={t('feedback.helpful', 'Helpful answer')}
        >
          <ThumbsUp
            className="w-5 h-5"
            fill={rating === 'positive' ? 'currentColor' : 'none'}
            strokeWidth={rating === 'positive' ? 0 : 2}
          />
        </button>
        <button
          type="button"
          onClick={() => handleFeedback('negative')}
          disabled={disabled || feedbackMutation.isPending || rating !== null}
          className={cn(
            buttonBase,
            rating === 'negative'
              ? "bg-error/10 text-error"
              : "text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 active:bg-neutral-200"
          )}
          aria-label={t('feedback.unhelpful', 'Unhelpful answer')}
        >
          <ThumbsDown
            className="w-5 h-5"
            fill={rating === 'negative' ? 'currentColor' : 'none'}
            strokeWidth={rating === 'negative' ? 0 : 2}
          />
        </button>

        {/* Bookmark button - navigates to detail if bookmarked, otherwise creates */}
        <button
          type="button"
          onClick={handleBookmark}
          disabled={disabled || createBookmark.isPending}
          className={cn(
            buttonBase,
            isBookmarked
              ? "bg-citation-100 text-citation-600 hover:bg-citation-200"
              : "text-neutral-400 hover:text-citation-600 hover:bg-citation-50 active:bg-citation-100"
          )}
          aria-label={isBookmarked ? t('feedback.viewBookmark', 'View bookmark') : t('feedback.saveBookmark', 'Save as bookmark')}
        >
          <Bookmark
            className="w-5 h-5"
            fill={isBookmarked ? 'currentColor' : 'none'}
            strokeWidth={isBookmarked ? 0 : 2}
          />
        </button>

    </div>
  )
}
